import { calcPrice } from '@/shared';
import { useCallback, useEffect, useMemo } from 'react';
import { useCart, useClient, usePrices, useTables } from '@/hooks';
import { ProductProps, PriceProductProps, TaxSubstitutionProps, ParametroPrecoBaseProps, PercentualFornecedorProps, PrecoPromocaoClienteProps } from '@/types';

export const useCalcPrice = () => {
  const { tables, setOnTables } = useTables();
  const { getLimit } = useCart();

  const dictProducts = useMemo(() => {
    if (!!tables.current.dicionario_produtos) return tables.current.dicionario_produtos

    const dicionario = (tables.current.produtos || []).reduce((acc, produto) => {
      acc[produto.COD_PRODUTO] = produto;
      return acc;
    }, {} as Record<number, ProductProps>);

    setOnTables(dicionario, 'dicionario_produtos');
    return dicionario;
  }, []);

  const { selectedClient } = useClient();
  const { catalog: hookCatalog, setProducts, isLoaded, isLoading: isLoadingCatalog, calculatedList } = usePrices();

  const substTrib = tables.current.cod_regime_tributario;
  const segmentos = useMemo(() => selectedClient?.COD_SEGMENTO?.map((c) => c.cod_segmento) || [], [selectedClient]);
  const condicoes = useMemo(() => selectedClient?.COD_PAGAMENTO?.map((i) => i.cod_condicao) || [], [selectedClient]);
  const catalog = useMemo(() => hookCatalog[selectedClient.COD_CLIENTE] || [], [hookCatalog, selectedClient.COD_CLIENTE]);

  const handlePrices = useCallback(
    (
      condicoes: number[],
      product: ProductProps,
      parametroPrecoBase: ParametroPrecoBaseProps[],
      percentualFornecedor: PercentualFornecedorProps[],
      listSubstTrib: TaxSubstitutionProps[],
      precoPromocao: PrecoPromocaoClienteProps,
      from: string
    ): PriceProductProps[] => {
      if (isLoaded.current[selectedClient.COD_CLIENTE]) return [] as PriceProductProps[];
      
      const precos = [] as PriceProductProps[];

      for (const cond of condicoes) {
        for (const precoJson of product?.PRECO || []) {
          // Embalagem base (geralmente UN) correspondente à QTD_EMBALAGEM do preço
          const embalagemBase = (product.EMBALAGEM || []).find((e) => e.QTD_EMBALAGEM === precoJson.QTD_EMBALAGEM);
          // Apenas adiciona fallback para múltiplos de venda (PK) quando houver
          const embalagensPK = (product.EMBALAGEM || []).filter(
            (e) => e.SG_EMBALAGEM === 'PK' && (e.QTD_MULTIPLO_VENDA || 0) > 1 && e.QTD_EMBALAGEM === precoJson.QTD_EMBALAGEM
          );
          const embalagens = [embalagemBase, ...embalagensPK].filter(Boolean) as any[];

          // Preço promocional por quantidade de embalagem (base unitária)
          const precoPromo = precoPromocao?.[selectedClient.COD_CLIENTE]?.[precoJson.QTD_EMBALAGEM]?.[precoJson.COD_PRODUTO]?.[selectedClient.COD_LOJA]?.[precoJson.NRO_SEGMENTO];
          const priceValidation: any = precoPromo?.VLR_PRECO ? precoPromo : precoJson;

          const percPreco = parametroPrecoBase?.find(
            (p) => p.NROCONDICAOPAGTO === cond && priceValidation.FAIXAACRFINANCEIRO === p.FAIXAACRFINANCEIRO
          );

          if (!percPreco) continue;

          // Itera por TODAS as embalagens compatíveis, gerando preço também para múltiplos de venda (PK)
          for (const embalagem of embalagens) {
            const percentualForcedor =
              percentualFornecedor?.find((forn) => precoJson.SEQFAMILIA === forn.SEQFAMILIA)?.PERACRESCDESC || 0;

            const isPrecoPromo =
              precoPromo && precoPromo.QTD_EMBALAGEM === embalagem?.QTD_EMBALAGEM && precoPromo.VLR_PRECO < precoJson.VLR_PRECO;

            const basePriceJson = isPrecoPromo ? precoPromo : precoJson;

            const precoIn: PriceProductProps = {
              ...precoJson,
              ...(isPrecoPromo && { COD_CLIENTE: (precoPromo as any).COD_CLIENTE }),
              COD_LOJA: selectedClient.COD_LOJA,
              COD_PRODUTO: precoJson.COD_PRODUTO,
              COD_TRIBUTACAO_PRODUTO: basePriceJson.COD_TRIBUTACAO_PRODUTO,
              DESTACA_ST: basePriceJson.DESTACA_ST,
              DT_FIM: '',
              DT_INICIO: '',
              FAIXAACRFINANCEIRO: basePriceJson.FAIXAACRFINANCEIRO,
              INDUSAACRESCTABVENDA: basePriceJson.INDUSAACRESCTABVENDA,
              NROCONDICAOPAGTO: cond,
              NRO_SEGMENTO: basePriceJson.NRO_SEGMENTO,
              // Usa a embalagem corrente: para PK garante QTD_MULTIPLO_VENDA correta
              QTD_EMBALAGEM: embalagem.QTD_EMBALAGEM,
              QTD_MULTIPLO_VENDA: embalagem.QTD_MULTIPLO_VENDA,
              SEQFAMILIA: basePriceJson.SEQFAMILIA,
              NROTABVENDA: `${selectedClient.COD_TABELA_VENDA}`,
              PERACRESCDESC: percentualForcedor,
              PERACRENTREGA: percPreco.PERACRENTREGA,
              PERACRFINANCEIRO: percPreco.PERACRFINANCEIRO,
              SG_EMBALAGEM: embalagem.SG_EMBALAGEM,
              TP_PROMO: basePriceJson.TP_PROMO,
              // Valor base por unidade; calcPrice cuidará de multiplicar por QTD_MULTIPLO_VENDA
              VLR_PRECO: basePriceJson.VLR_PRECO,
              // Pauta em nível unitário; calcPrice/ST cuidará da multiplicação/uso correto
              VLR_PAUTA: precoJson.VLR_PAUTA,
            } as PriceProductProps;

            precos.push(precoIn);
          }
        }
      }

      return calcPrice(selectedClient, precos, listSubstTrib);
    },
    [selectedClient]
  );

  const loadStates = useCallback(
    async (catalogList: ProductProps[], ordered = true, filterStock = false, forceLoad = false) => {
      if (catalog.length && !forceLoad) return catalogList;

      let list = catalogList.reduce((acc, product) => {
        if (filterStock) {
          const limit = getLimit(product.COD_PRODUTO);
          const emb = product?.EMBALAGEM || [];
          const hasEmbLimit = emb.reduce((accEmb, emb) => accEmb || limit >= emb.QTD_EMBALAGEM, false);
          if (limit <= 0 || !emb.length || !hasEmbLimit) return acc;
        }

        return [...acc, product];
      }, [] as ProductProps[]);

      list = list.sort((a, b) => {
        if (a.DESC_PRODUTO < b.DESC_PRODUTO) return -1;
        if (a.DESC_PRODUTO > b.DESC_PRODUTO) return 1;
        return 0;
      });

      setProducts(selectedClient.COD_CLIENTE, list);

      return list;
    },
    [getLimit, selectedClient, catalog.length]
  );

  const getProduct = useCallback(
    (id: number) => {
      return catalog?.find((item) => item?.COD_PRODUTO === id);
    },
    [catalog]
  );

  const getCatalog = useCallback(
    async (ordered = true, timer?: number, filterStock = false, forceLoad?: boolean) => {
      if (!selectedClient?.COD_CLIENTE || !dictProducts) return;
      if (!tables.current.produtos || isLoaded.current[selectedClient.COD_CLIENTE] || isLoadingCatalog.current) {
        return isLoaded.current[selectedClient.COD_CLIENTE];
      }

      if (catalog.length && !forceLoad) return;

      isLoadingCatalog.current = true;
      const segmentosMap = segmentos.reduce((acc, s) => {
        acc[s] = true;
        return acc;
      }, {} as Record<number, boolean>);

      const loadedCatalog = Object.values(dictProducts)
        .map((p: ProductProps) => ({
          ...p,
          EMBALAGEM: p.EMBALAGEM?.filter((e) => segmentosMap[e.COD_SEGMENTO]),
        }))
        .filter((p) => p.EMBALAGEM?.length) as ProductProps[];

      const list = await loadStates(loadedCatalog, ordered, filterStock, forceLoad);

      await new Promise((resolve) => setTimeout(() => resolve(true), timer || 1200));
      if (!list.length) {
        setProducts(selectedClient.COD_CLIENTE, list);
        isLoaded.current[selectedClient.COD_CLIENTE] = true;
      }

      isLoadingCatalog.current = false;
      return list;
    },
    [catalog, selectedClient, dictProducts, loadStates]
  );

  const reloadCatalog = useCallback(async (ordered?: boolean, timer?: number, filterStock = false) => {
    if (!dictProducts) return;
    setProducts(selectedClient.COD_CLIENTE, []);
    await getCatalog(ordered, timer, filterStock, true);
  }, [dictProducts]);

  useEffect(() => {
    if (hookCatalog[selectedClient.COD_CLIENTE] || !selectedClient?.COD_CLIENTE || !dictProducts) return;

    getCatalog();
  }, [hookCatalog, selectedClient.COD_CLIENTE, dictProducts]);

  const findPrice = (price: PriceProductProps[], option_uid: string, paymentCondition: number) => {
    const [packing, quantity] = option_uid?.split('_');
    const correctPrice = price
      .filter((p) => p.COD_LOJA === selectedClient?.COD_LOJA)
      ?.find((p) => {
        // Para múltiplos de venda (ex.: PK), procurar pela própria embalagem e pelo QTD_MULTIPLO_VENDA
        if (packing === 'PK' || (p?.QTD_MULTIPLO_VENDA || 0) > 1) {
          return (
            p.SG_EMBALAGEM === packing && p.QTD_MULTIPLO_VENDA?.toString() === quantity && p.NROCONDICAOPAGTO === paymentCondition
          );
        }
        // Caso padrão (UN ou outras embalagens sem múltiplo)
        return p.SG_EMBALAGEM === packing && p.QTD_EMBALAGEM?.toString() === quantity && p.NROCONDICAOPAGTO === paymentCondition;
      });
    return correctPrice || ({} as PriceProductProps);
  };

  const getPrices = useCallback(
    (product: ProductProps): PriceProductProps[] => {
      const keyCalculated = `${selectedClient.COD_CLIENTE}-${product.COD_PRODUTO}`;
      if (calculatedList.current[keyCalculated]) return calculatedList.current[keyCalculated];

      const codLoja = selectedClient.COD_LOJA;
      const tabVenda = selectedClient.COD_TABELA_VENDA;
      const percentual = tables.current.percentual_fornecedor as any;
      const listSubstTrib = tables.current.cod_regime_tributario as any;
      const parametroPrecoPromocao = tables.current.precoPromocaoCliente;
      const parametroPrecoBase = tables.current.parametro_preco_base as any;
      const percentualFornecedor = percentual.filter((p: PercentualFornecedorProps) => `${p.NROTABVENDA}` === `${tabVenda}`);

      const parametroPreco = parametroPrecoBase.filter(
        (p: ParametroPrecoBaseProps) =>
          `${p.NROTABVENDA}` === `${tabVenda}` && condicoes.includes(p.NROCONDICAOPAGTO) && codLoja === p.COD_LOJA
      );

      const precos = handlePrices(
        condicoes,
        product,
        parametroPreco,
        percentualFornecedor,
        listSubstTrib,
        parametroPrecoPromocao,
        'useCalcPrice'
      );
      
      if (precos.length > 0) calculatedList.current[keyCalculated] = precos;
      return precos;
    },
    [selectedClient]
  );

  return {
    catalog,
    substTrib,
    isLoadingCatalog,
    isLoaded: isLoaded.current,
    getProduct,
    getCatalog,
    reloadCatalog,
    handlePrices,
    findPrice,
    getPrices,
  };
};
